import { History } from '@app/home/models/history.model';
import { SimulationMetadata, SimulationType } from '@core/models/simulation-metadata.model';
import { CoreActions } from '@core/store';
import { createReducer, on } from '@ngrx/store';
import { DateTime, DurationObjectUnits } from 'luxon';

import { MetricChangeHistory, TimestreamMetric } from '../../../models/metric.model';
import { TableHelper } from '../helpers/table.helper';
import { ChartStep } from '../models/chart-step';
import { Interval } from '../models/interval';
import { SelectedRange } from '../models/revogrid-definition.model';
import { PhaseShiftHelper } from '../helpers/phase-shift.helper';

import * as AnalysisActions from './analysis.actions';
import { UtilityHelper } from '@core/helpers/utility.helper';

export const analysisFeatureKey = 'analysis';

export interface PreviousDate {
  minute?: number;
  hour?: number;
  day?: number;
  month?: number;
  year?: number;
}

export interface State {
  loading: boolean;
  panelLoading: boolean;
  compute: boolean;
  showTreeView: boolean;
  selectedTab: 'chart' | 'table';
  startDate: string;
  endDate: string;
  previousDate: PreviousDate;
  metrics: Array<TimestreamMetric>;
  interval: Interval;
  tableStepSize: number;
  chartStep: ChartStep;
  comparaisonExpanded: boolean;
  currentTableSliceIndex: number;
  prsim: SimulationMetadata;
  hreko: SimulationMetadata;
  selectedPanel: 'edit' | 'transfert' | 'history' | 'launch-simulation' | 'change-history' | null;
  selectedRange: SelectedRange;
  history: Array<History>;
  changeHistory: MetricChangeHistory[];
  LastChangeInHistory: MetricChangeHistory;
  phaseShiftEnabled: boolean;
}

const initialState: State = {
  loading: true,
  panelLoading: false,
  compute: false,
  showTreeView: true,
  selectedTab: null,
  startDate: null,
  endDate: null,
  previousDate: { minute: null, hour: null, day: null, month: null, year: null },
  metrics: [],
  interval: Interval.THREE_MINUTES,
  tableStepSize: 1,
  chartStep: ChartStep.LAST_TWO_DAYS,
  comparaisonExpanded: true,
  currentTableSliceIndex: 0,
  prsim: null,
  hreko: null,
  selectedPanel: null,
  selectedRange: null,
  history: [],
  changeHistory: [],
  LastChangeInHistory: null,
  phaseShiftEnabled: false
};

export const reducer = createReducer(
  initialState,
  on(AnalysisActions.resetState, state => initialState),
  on(AnalysisActions.init, (state, { endDate, tab, metrics, prsim, hreko }) => {
    const hasMultiplePropertiesSelected = metrics.length > 1;
    let end =
      tab === 'chart'
        ? endDate
        : hasMultiplePropertiesSelected
          ? endDate
          : endDate.minute !== 0
            ? endDate.plus({ hours: 1 }).set({ minute: 0 })
            : endDate;
    let start =
      tab === 'chart'
        ? end.minus({ days: 2 }).plus({ minutes: 3 })
        : hasMultiplePropertiesSelected
          ? end.minus({ minutes: 57 })
          : end.minus({ hours: 6 }).set({ minute: 3 });

    switch (state.interval) {
      case Interval.HOURS:
        end = end.plus({ day: 1 }).set({ hour: 6, minute: 0 });
        start = end.minus({ day: 1 }).plus({ minute: 3 });
        break;
      case Interval.DAYS:
        end = end.plus({ month: 1 }).set({ day: 1, hour: 6, minute: 0 });
        start = end.minus({ month: 1 }).plus({ minute: 3 });
        break;
      case Interval.MONTHS:
        end = end.plus({ month: 1 }).set({ day: 1, hour: 6, minute: 0 });
        start = end.minus({ year: 1 }).plus({ minute: 3 });
        break;
    }

    return {
      ...state,
      startDate: start.toISO(),
      endDate: end.toISO(),
      selectedTab: tab,
      prsim,
      hreko,
      loading: true,
      metrics
    };
  }),
  on(AnalysisActions.toggleTreeView, state => ({
    ...state,
    showTreeView: !state.showTreeView
  })),
  on(AnalysisActions.switchInterval, (state, { interval, step }) => {
    state = { ...state, metrics: state.metrics.map(metric => ({ ...metric, phaseShift: 0, loadedPhaseShift: 0 })) };
    const tableStepSize = 1;
    let chartStep;
    if (step) {
      chartStep = step;
    } else {
      chartStep = getDefaultChartStep(interval);
    }
    const range = calculateRange(
      state.endDate,
      state.selectedTab,
      interval,
      tableStepSize,
      chartStep,
      state.metrics.length,
      state.selectedTab === 'table',
      'interval',
      state.previousDate
    );
    return {
      ...state,
      interval,
      tableStepSize,
      chartStep,
      startDate: range.startDate,
      endDate: range.endDate,
      previousDate: range.previousDate,
      loading: state.metrics.length > 0 ? true : state.loading,
      compute: state.metrics.length > 0 ? true : state.compute,
      selectedRange: null
    };
  }),
  on(AnalysisActions.switchTab, (state, { tab }) => {
    state = exitPhaseShift(state);
    const interval = state.interval === Interval.MONTHS && state.metrics.length < 2 ? Interval.DAYS : state.interval;
    const tableStepSize = 1;
    const chartStep = getDefaultChartStep(interval);
    const range = calculateRange(
      state.endDate,
      tab,
      interval,
      tableStepSize,
      chartStep,
      state.metrics.length,
      tab === 'table'
    );
    return {
      ...state,
      selectedTab: tab,
      interval,
      tableStepSize,
      chartStep,
      startDate: range.startDate,
      endDate: range.endDate,
      loading: state.metrics.length > 0 ? true : false,
      selectedRange: null
    };
  }),
  on(AnalysisActions.toggleComparaisonExpandedHeader, state => {
    return {
      ...state,
      comparaisonExpanded: !state.comparaisonExpanded
    };
  }),
  on(AnalysisActions.loadMetricsSuccess, (state, { metrics }) => {
    const maxColumns = TableHelper.getMaxColumns(state.comparaisonExpanded);
    return {
      ...state,
      metrics,
      currentTableSliceIndex: metrics.length > maxColumns ? metrics.length - maxColumns : state.currentTableSliceIndex,
      loading: false,
      compute: false
    };
  }),
  on(AnalysisActions.selectMetric, (state, { metric }) => {
    if (state.selectedTab === 'table' && state.metrics.length <= 1) {
      const range = calculateRange(
        state.endDate,
        state.selectedTab,
        state.interval,
        state.tableStepSize,
        state.chartStep,
        state.metrics.length + 1,
        false
      );
      return {
        ...state,
        loading: true,
        compute: true,
        startDate: range.startDate,
        endDate: range.endDate,
        selectedRange: null
      };
    } else {
      return state;
    }
  }),
  on(AnalysisActions.unselectMetric, (state, { metric }) => {
    const maxColumns = TableHelper.getMaxColumns(state.comparaisonExpanded);
    const newState: State = {
      ...state,
      metrics: state.metrics.filter(
        m => !(m.key === metric.key && m.property === metric.property && m.simulationType === metric.simulationType)
      ),
      currentTableSliceIndex:
        state.metrics.length > maxColumns && state.currentTableSliceIndex > 0
          ? state.currentTableSliceIndex - 1
          : state.currentTableSliceIndex,
      selectedRange: null
    };

    if (!state.comparaisonExpanded && newState.metrics.length <= TableHelper.MAX_COLUMNS) {
      newState.comparaisonExpanded = true;
    }

    if (state.selectedTab === 'table' && newState.metrics.length === 1) {
      if (state.interval === Interval.MONTHS) {
        newState.interval = Interval.DAYS;
      }
      const range = calculateRange(
        state.endDate,
        state.selectedTab,
        newState.interval,
        state.tableStepSize,
        state.chartStep,
        newState.metrics.length
      );
      newState.startDate = range.startDate;
      newState.endDate = range.endDate;
    }

    if (state.metrics.length === 2 && newState.metrics.length === 1) {
      newState.loading = true;
      newState.compute = true;
    }

    return newState;
  }),
  on(AnalysisActions.updateDateRange, (state, { endDate, startDate, source }) => {
    if (startDate) {
      const range = source === 'custom' ? calculateCustomRange(startDate, endDate) : { startDate, endDate };

      if (range.endDate <= range.startDate) {
        return state;
      }

      return {
        ...state,
        startDate: range.startDate.toISO(),
        endDate: range.endDate.toISO(),
        chartStep: ChartStep.CUSTOM,
        compute: true
      };
    } else {
      const chartStep = state.chartStep === ChartStep.CUSTOM ? getDefaultChartStep(state.interval) : state.chartStep;
      const range = calculateRange(
        endDate.toISO(),
        state.selectedTab,
        state.interval,
        state.tableStepSize,
        chartStep,
        state.metrics.length,
        state.selectedTab === 'table',
        source
      );
      return {
        ...state,
        startDate: range.startDate,
        endDate: range.endDate,
        chartStep,
        compute: source !== 'single-step'
      };
    }
  }),
  on(AnalysisActions.changeChartStep, (state, { step }) => {
    if (step !== ChartStep.CUSTOM) {
      const startDate = DateTime.fromISO(state.endDate)
        .minus(convertStepToDuration(step))
        .plus({ minutes: 3 });
      return {
        ...state,
        chartStep: step,
        startDate: startDate.toISO()
      };
    } else {
      return {
        ...state,
        chartStep: step
      };
    }
  }),
  on(AnalysisActions.changeTableStepSize, (state, { stepSize }) => {
    const range = calculateRange(
      state.endDate,
      state.selectedTab,
      state.interval,
      stepSize,
      state.chartStep,
      state.metrics.length,
      state.metrics.length > 1 ? false : true,
      null,
      state.previousDate
    );
    return {
      ...state,
      tableStepSize: stepSize,
      startDate: range.startDate,
      endDate: range.endDate,
      previousDate: range.previousDate
    };
  }),
  on(AnalysisActions.nextComparaisonHeaderSlice, state => ({
    ...state,
    currentTableSliceIndex: state.currentTableSliceIndex + 1
  })),
  on(AnalysisActions.previousComparaisonHeaderSlice, state => ({
    ...state,
    currentTableSliceIndex: state.currentTableSliceIndex - 1
  })),
  on(AnalysisActions.changeTableStepSize, (state, { stepSize }) => ({
    ...state,
    tableStepSize: stepSize
  })),
  on(AnalysisActions.previousSlice, state => {
    const startDate = DateTime.fromISO(state.startDate);
    const endDate = DateTime.fromISO(state.endDate);
    let duration: DurationObjectUnits;
    if (state.chartStep === ChartStep.CUSTOM) {
      let diff = Math.round(endDate.diff(startDate, 'minutes').minutes / 2);
      const mod = diff % 3;
      if (mod > 0) {
        diff = mod === 1 ? diff - 1 : diff + 1;
      }
      duration = { minutes: diff };
    } else if (state.interval === Interval.THREE_MINUTES) {
      duration = { minutes: endDate.diff(startDate.minus({ minutes: 3 }), 'minutes').minutes / 2 };
    } else {
      duration = { days: Math.round(endDate.diff(startDate.minus({ minutes: 3 }), 'days').days / 2) };
    }

    return {
      ...state,
      startDate: startDate.minus(duration).toISO(),
      endDate: endDate.minus(duration).toISO()
    };
  }),
  on(AnalysisActions.nextSlice, state => {
    const startDate = DateTime.fromISO(state.startDate);
    const endDate = DateTime.fromISO(state.endDate);
    let duration: DurationObjectUnits;
    if (state.chartStep === ChartStep.CUSTOM) {
      let diff = Math.round(endDate.diff(startDate, 'minutes').minutes / 2);
      const mod = diff % 3;
      if (mod > 0) {
        diff = mod === 1 ? diff - 1 : diff + 1;
      }
      duration = { minutes: diff };
    } else if (state.interval === Interval.THREE_MINUTES) {
      duration = { minutes: endDate.diff(startDate.minus({ minutes: 3 }), 'minutes').minutes / 2 };
    } else {
      duration = { days: Math.round(endDate.diff(startDate.minus({ minutes: 3 }), 'days').days / 2) };
    }

    return {
      ...state,
      startDate: startDate.plus(duration).toISO(),
      endDate: endDate.plus(duration).toISO()
    };
  }),
  on(AnalysisActions.toggleComparaisonExpandedHeader, state => ({
    ...state,
    currentTableSliceIndex: 0
  })),
  on(AnalysisActions.selectRange, (state, { selectedRange }) => ({
    ...state,
    selectedRange
  })),
  on(AnalysisActions.updateRangeData, (state, update) => ({
    ...state,
    panelLoading: true,
    selectedRange: {
      ...state.selectedRange,
      startMetric: {
        ...state.selectedRange.startMetric,
        value: update.data.startValue
      },
      endMetric: {
        ...state.selectedRange.endMetric,
        value: update.data.endValue ?? state.selectedRange.endMetric.value
      }
    }
  })),
  on(AnalysisActions.transfertRangeData, state => ({
    ...state,
    panelLoading: true
  })),
  on(AnalysisActions.updateRangeDataError, state => ({
    ...state,
    panelLoading: false
  })),
  on(AnalysisActions.updateRangeDataSuccess, state => ({
    ...state,
    panelLoading: false,
    selectedPanel: null
  })),
  on(AnalysisActions.togglePanel, (state, { panel }) => {
    const selectedPanel = panel === state.selectedPanel ? null : panel;
    return {
      ...state,
      selectedPanel,
      history: selectedPanel === null ? [] : state.history,
      panelLoading: selectedPanel === 'history'
    };
  }),
  on(AnalysisActions.getHistoryForKeySuccess, (state, { history }) => ({
    ...state,
    history,
    panelLoading: false
  })),
  on(AnalysisActions.getHistoryForKeyError, state => ({
    ...state,
    panelLoading: false
  })),
  on(CoreActions.updateLatestSimulationResult, (state, { simulationResult }) => ({
    ...state,
    prsim:
      simulationResult.simulationType === SimulationType.PRSIM
        ? { ...state.prsim, max: simulationResult.date, simulationResult }
        : state.prsim,
    hreko:
      simulationResult.simulationType === SimulationType.HREKO
        ? { ...state.hreko, max: simulationResult.date, simulationResult }
        : state.hreko
  })),
  on(AnalysisActions.addChangeHistorySuccess, (state, { newHistory }) => ({
    ...state,
    changeHistory: [...state.changeHistory, newHistory]
  })),
  on(AnalysisActions.removeLastChangeInHistory, state => ({
    ...state,
    changeHistory: [...state.changeHistory].splice(0, state.changeHistory.length - 1),
    LastChangeInHistory: state.changeHistory.length > 0 ? state.changeHistory[state.changeHistory.length - 1] : null
  })),
  on(AnalysisActions.resetChangeHistory, state => ({
    ...state,
    changeHistory: [],
    LastChangeInHistory: null
  })),
  on(AnalysisActions.togglePhaseShift, state =>
    state.phaseShiftEnabled ? exitPhaseShift(state) : { ...state, phaseShiftEnabled: true }
  ),
  on(AnalysisActions.changePhaseShift, (state, { index, phaseShift }) => ({
    ...state,
    metrics: state.metrics.map((metric, i) =>
      i === index && (metric.phaseShift || 0) !== phaseShift ? { ...metric, phaseShift, uptodate: false } : metric
    )
  })),
  on(AnalysisActions.duplicatePhaseShiftMetric, (state, { index }) =>
    state.metrics[index]
      ? { ...state, metrics: [...state.metrics, { ...state.metrics[index], isPhaseShiftCopy: true }] }
      : state
  ),
  on(AnalysisActions.removePhaseShiftMetric, (state, { index }) => ({
    ...state,
    metrics: state.metrics.filter((metric, i) => i !== index || !metric.isPhaseShiftCopy)
  }))
);

const exitPhaseShift = (state: State): State => {
  if (!state.phaseShiftEnabled) {
    return state;
  }
  const referenceShift = state.metrics[0]?.phaseShift || 0;
  return {
    ...state,
    phaseShiftEnabled: false,
    startDate: PhaseShiftHelper.shiftDate(DateTime.fromISO(state.startDate), referenceShift).toISO(),
    endDate: PhaseShiftHelper.shiftDate(DateTime.fromISO(state.endDate), referenceShift).toISO(),
    metrics: state.metrics
      .filter(metric => !metric.isPhaseShiftCopy)
      .map(metric => ({
        ...metric,
        phaseShift: 0,
        loadedPhaseShift: (metric.loadedPhaseShift || 0) - referenceShift,
        uptodate: metric.uptodate && (metric.phaseShift || 0) === referenceShift
      }))
  };
};

const savePreviousDate = (editedDate: DateTime, savedDate: PreviousDate, scopes: Array<string>): void => {
  if (savedDate) {
    scopes.forEach(scope => {
      savedDate[scope] = editedDate[scope];
    });
  }
};

const applyPreviousDate = (editedDate: DateTime, savedDate: PreviousDate): DateTime => {
  const scopes = ['minute', 'hour', 'day', 'month', 'year'];
  if (savedDate) {
    scopes.forEach(scope => {
      if (savedDate[scope]) {
        editedDate = editedDate.set({ [scope]: savedDate[scope] });
        delete savedDate[scope];
      }
    });
  }
  return editedDate;
};

const calculateRange = (
  endDate: string,
  selectedTab: 'chart' | 'table',
  interval: Interval,
  tableStepSize: number,
  chartStep: ChartStep,
  nbMetrics: number,
  updateEndDate = true,
  source: 'calendar' | 'single-step' | 'next-step' | 'interval' | 'custom' = null,
  previousDate: PreviousDate = null
): { startDate: string; endDate: string; previousDate: PreviousDate } => {
  let end: DateTime = DateTime.fromISO(endDate);
  let start = null as DateTime;
  const savedDate = UtilityHelper.copy(previousDate) as PreviousDate;

  if (selectedTab === 'chart') {
    end = end.set({ second: 0, millisecond: 0 });
    start = end.minus(convertStepToDuration(chartStep)).plus({ minutes: 3 });
  } else {
    switch (interval) {
      case Interval.THREE_MINUTES: {
        end = applyPreviousDate(end, savedDate);
        if (updateEndDate) {
          if (end.minute !== 0) {
            savePreviousDate(end, savedDate, ['hour']);
            end = end.plus({ hours: 1 });
          }
          savePreviousDate(end, savedDate, ['minute']);
          end = end.set({ minute: 0, second: 0, millisecond: 0 });
        }
        start = end.minus(nbMetrics > 1 ? { hours: tableStepSize } : { hours: 6 }).plus({ minutes: 3 });
        break;
      }
      case Interval.HOURS: {
        end = applyPreviousDate(end, savedDate);
        if (updateEndDate) {
          if (source === 'interval') {
            if (end.hour > 6) {
              savePreviousDate(end, savedDate, ['day']);
              end = end.plus({ days: 1 });
            }
          }
          savePreviousDate(end, savedDate, ['minute', 'hour']);
          end = end.set({ minute: 0, second: 0, millisecond: 0, hour: 6 });
        }
        if (source === 'calendar') {
          end = end.plus({ days: 1 });
        }
        start = end.minus(nbMetrics > 1 ? { days: tableStepSize } : { days: 6 }).plus({ minutes: 3 });
        break;
      }
      case Interval.DAYS: {
        end = applyPreviousDate(end, savedDate);
        if (updateEndDate) {
          if (source === 'interval') {
            if (end.day > 1 || end.hour > 6) {
              savePreviousDate(end, savedDate, ['month']);
              end = end.plus({ months: 1 });
            }
          }
          savePreviousDate(end, savedDate, ['minute', 'hour', 'day']);
          end = end.set({ minute: 0, second: 0, millisecond: 0, hour: 6, day: 1 });
        }
        if (source === 'calendar') {
          end = end.plus({ months: 1 });
        }
        start = end.minus(nbMetrics > 1 ? { months: tableStepSize } : { months: 6 }).plus({ minutes: 3 });
        break;
      }
      case Interval.MONTHS: {
        if (updateEndDate) {
          if (source === 'interval') {
            if (end.day > 1 || end.hour > 6) {
              end = end.plus({ months: 1 });
            }
          }
          end = end.set({ minute: 0, second: 0, millisecond: 0, hour: 6, day: 1 });
        }
        if (source === 'calendar') {
          end = end.plus({ months: 1 });
        }
        start = end.minus(nbMetrics > 1 ? { years: tableStepSize } : { years: 6 }).plus({ minutes: 3 });
        break;
      }
    }
  }

  return {
    startDate: start.toISO(),
    endDate: end.toISO(),
    previousDate: savedDate
  };
};

const calculateCustomRange = (startDate: DateTime, endDate: DateTime): { startDate: DateTime; endDate: DateTime } => {
  return {
    startDate: startDate.set({ second: 0, millisecond: 0 }),
    endDate: endDate.set({ second: 0, millisecond: 0 })
  };
};

const getDefaultChartStep = (interval: Interval): ChartStep => {
  switch (interval) {
    case Interval.THREE_MINUTES: {
      return ChartStep.LAST_TWO_DAYS;
    }
    case Interval.HOURS: {
      return ChartStep.LAST_WEEK;
    }
    case Interval.DAYS: {
      return ChartStep.LAST_TWO_MONTHS;
    }
    case Interval.MONTHS: {
      return ChartStep.LAST_TWO_YEARS;
    }
  }
};

const convertStepToDuration = (step: ChartStep): DurationObjectUnits => {
  switch (step) {
    case ChartStep.LAST_HOUR: {
      return { hours: 1 };
    }
    case ChartStep.LAST_DAY: {
      return { days: 1 };
    }
    case ChartStep.LAST_TWO_DAYS: {
      return { days: 2 };
    }
    case ChartStep.LAST_WEEK: {
      return { days: 7 };
    }
    case ChartStep.LAST_MONTH: {
      return { months: 1 };
    }
    case ChartStep.LAST_TWO_MONTHS: {
      return { months: 2 };
    }
    case ChartStep.LAST_YEAR: {
      return { years: 1 };
    }
    case ChartStep.LAST_TWO_YEARS: {
      return { years: 2 };
    }
  }
};
